import React, { useState, useMemo } from 'react';
import StatCard from '../components/StatCard';
import TrendBadge from '../components/TrendBadge';
import DataTable from '../components/DataTable';
import useAPI from '../hooks/useAPI';
import { getTechnicianKPIs } from '../services/api';

const TABLE_COLUMNS = [
  { key: 'rank', label: '#', align: 'center' },
  { key: 'name', label: 'Technician' },
  {
    key: 'overall_score',
    label: 'Overall',
    align: 'center',
    render: v => <span style={{ fontWeight: 700, color: v >= 70 ? '#00b894' : v >= 50 ? '#e17055' : v > 0 ? '#d63031' : '#b2bec3' }}>{v || '-'}</span>,
  },
  { key: 'professionalism', label: 'Prof.', align: 'center',
    render: v => <span style={{ color: v > 0 ? undefined : '#ccc' }}>{v || '-'}</span> },
  { key: 'efficiency', label: 'Eff.', align: 'center',
    render: v => <span style={{ color: v > 0 ? undefined : '#ccc' }}>{v || '-'}</span> },
  { key: 'creativity', label: 'Cre.', align: 'center',
    render: v => <span style={{ color: v > 0 ? undefined : '#ccc' }}>{v || '-'}</span> },
  { key: 'total_resolutions', label: 'Fixes', align: 'right',
    render: v => <span style={{ color: v > 0 ? undefined : '#ccc' }}>{v}</span> },
  {
    key: 'success_rate',
    label: 'Success %',
    align: 'right',
    render: v => v > 0
      ? <span style={{ color: v >= 90 ? '#00b894' : v >= 70 ? '#e17055' : '#d63031' }}>{v}%</span>
      : <span style={{ color: '#ccc' }}>-</span>,
  },
  { key: 'trend', label: 'Trend', render: v => v ? <TrendBadge trend={v} /> : <span style={{ color: '#ccc' }}>-</span> },
  {
    key: 'strong_areas',
    label: 'Strong In',
    render: v => v?.length > 0 ? v.join(', ') : '-',
  },
];

function CountrySection({ flag, country, technicians, startRank }) {
  const active = technicians.filter(t => t.total_resolutions > 0);
  // Re-number ranks within section
  const numbered = technicians.map((t, i) => ({ ...t, rank: startRank + i }));

  return (
    <div style={styles.countrySection}>
      <div style={styles.countryHeader}>
        <span style={styles.countryFlag}>{flag}</span>
        <span style={styles.countryName}>{country}</span>
        <span style={styles.countryCount}>
          {active.length} active / {technicians.length} total
        </span>
      </div>
      <DataTable columns={TABLE_COLUMNS} data={numbered} />
    </div>
  );
}

export default function TechnicianKPI() {
  const [days, setDays] = useState(90);
  const { data, loading } = useAPI(() => getTechnicianKPIs(days), [days]);

  const { israelTechs, indiaTechs } = useMemo(() => {
    const rankings = data?.rankings || [];
    return {
      israelTechs: rankings.filter(t => t.country === 'Israel'),
      indiaTechs: rankings.filter(t => t.country === 'India'),
    };
  }, [data]);

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Loading KPIs...</div>;

  const rankings = data?.rankings || [];
  // Top 3 active (with fixes)
  const topActive = rankings.filter(t => t.total_resolutions > 0).slice(0, 3);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Technician Performance</h1>
          <p style={{ fontSize: 14, color: '#636e72', marginTop: 4 }}>
            {data?.active_technicians || 0} active of {data?.total_technicians || 0} technicians
          </p>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))} style={styles.select}>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 6 months</option>
        </select>
      </div>

      {/* Top 3 cards */}
      {topActive.length >= 3 && (
        <div style={styles.topThree}>
          {topActive.map((tech, i) => (
            <div key={tech.name} style={{ ...styles.topCard, borderTopColor: ['#ffd700', '#c0c0c0', '#cd7f32'][i] }}>
              <div style={styles.topRank}>#{i + 1}</div>
              <div style={styles.topName}>{tech.name}</div>
              <div style={styles.topScore}>{tech.overall_score}</div>
              <div style={styles.topDimensions}>
                <div>Prof: {tech.professionalism}</div>
                <div>Eff: {tech.efficiency}</div>
                <div>Cre: {tech.creativity}</div>
              </div>
              <div style={styles.topStats}>
                {tech.total_resolutions} fixes | {tech.success_rate}% success
              </div>
              <TrendBadge trend={tech.trend} />
            </div>
          ))}
        </div>
      )}

      {/* Israel Section */}
      {israelTechs.length > 0 && (
        <CountrySection
          flag="🇮🇱"
          country="Israel"
          technicians={israelTechs}
          startRank={1}
        />
      )}

      {/* India Section */}
      {indiaTechs.length > 0 && (
        <CountrySection
          flag="🇮🇳"
          country="India"
          technicians={indiaTechs}
          startRank={israelTechs.length + 1}
        />
      )}

      {/* Dimension explanations */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <strong>Professionalism</strong>: Fix quality, documentation completeness, parts recording
        </div>
        <div style={styles.legendItem}>
          <strong>Efficiency</strong>: Throughput (fixes/day), total volume
        </div>
        <div style={styles.legendItem}>
          <strong>Creativity</strong>: Variety of error types handled, root cause identification, infrastructure findings
        </div>
      </div>
    </div>
  );
}

const styles = {
  select: {
    padding: '8px 12px',
    borderRadius: 6,
    border: '1px solid #ddd',
    fontSize: 13,
    background: '#fff',
  },
  topThree: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    margin: '20px 0 32px',
  },
  topCard: {
    background: '#fff',
    borderRadius: 10,
    padding: 20,
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    borderTop: '4px solid #ccc',
  },
  topRank: { fontSize: 14, color: '#636e72', fontWeight: 600 },
  topName: { fontSize: 16, fontWeight: 700, margin: '8px 0 4px' },
  topScore: { fontSize: 36, fontWeight: 700, color: '#6c5ce7' },
  topDimensions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    fontSize: 12,
    color: '#636e72',
    margin: '8px 0',
  },
  topStats: { fontSize: 12, color: '#999', marginBottom: 6 },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  legend: {
    marginTop: 24,
    padding: 16,
    background: '#fff',
    borderRadius: 10,
    fontSize: 13,
    color: '#636e72',
  },
  legendItem: { padding: '4px 0' },
  countrySection: {
    marginBottom: 28,
  },
  countryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '2px solid #6c5ce7',
  },
  countryFlag: { fontSize: 22 },
  countryName: { fontSize: 18, fontWeight: 700, color: '#2d3436' },
  countryCount: { fontSize: 13, color: '#636e72', marginLeft: 'auto' },
};
